import { reactive, ref, computed } from "vue";
import useVuelidate from "@vuelidate/core";

export function useRowForm(rulesFn) {
    const activeRow = ref(null);               // PrimeVue row object
    const original = ref(null);                // deep copy for cancel
    const formData = reactive({});             // isolated form data

    // Build a reactive rules object (rulesFn may be a factory)
    const validations = computed(() => (typeof rulesFn === "function" ? rulesFn(formData) : rulesFn));

    // Create the Vuelidate instance during setup (so it has access to the component instance)
    const v$ = useVuelidate(validations, formData, { $lazy: true, $autoDirty: true });

    function initRow(rowData) {
        activeRow.value = rowData;
        original.value = JSON.parse(JSON.stringify(rowData));

        // replace formData contents while keeping the reactive object reference
        Object.keys(formData).forEach(k => delete formData[k]);
        Object.assign(formData, rowData);

        // Reset validation state for the new row
        if (v$?.value && typeof v$.value.$reset === 'function') {
            v$.value.$reset();
        }
    }

    async function validate() {
        // If no validation instance, consider valid
        if (!v$) return true;

        // Touch to ensure errors are visible for $lazy validations
        if (v$?.value && typeof v$.value.$touch === 'function') {
            v$.value.$touch();
        }

        // Use Vuelidate's $validate which touches and evaluates async validators
        const valid = await (v$?.value && typeof v$.value.$validate === 'function' ? v$.value.$validate() : true);
        return !!valid;
    }

    function cancel() {
        if (!original.value) return;
        // restore the active row object
        Object.assign(activeRow.value, original.value);
        // restore formData to original as well
        Object.keys(formData).forEach(k => delete formData[k]);
        Object.assign(formData, JSON.parse(JSON.stringify(original.value)));
        // reset validation state if available
        if (v$?.value && typeof v$.value.$reset === 'function') {
            v$.value.$reset();
        }
    }

    function commit() {
        if (!activeRow.value) return;
        // copy formData back to the active row object (deep copy to avoid refs leaking)
        Object.assign(activeRow.value, JSON.parse(JSON.stringify(formData)));
        // mark validation as committed (useful for Vuelidate groups)
        if (v$?.value && typeof v$.value.$commit === 'function') {
            v$.value.$commit();
        }
        // update the original snapshot
        original.value = JSON.parse(JSON.stringify(activeRow.value));
    }

    function normalize(normalizerFn) {
        return normalizerFn ? normalizerFn(formData) : { ...formData };
    }

    return {
        activeRow,
        formData,
        v$,
        initRow,
        validate,
        cancel,
        commit,
        normalize,
    };
}
